import type { AssignmentConflict } from '../../types/cockpit.types'

/**
 * AP-35 Detailblatt 11 5.4 — die Konflikte der Einteilung im Klartext.
 * Hinweise, keine Sperren: Der Dialog zeigt sie und lässt trotzdem bestätigen.
 */
const tag = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`

export function konfliktText(k: AssignmentConflict, name: (userId?: string) => string, schritt: (stepId?: string) => string): string {
  const tage = (k.dates ?? []).map(tag).join(', ')
  switch (k.code) {
    case 'ASSIGN_ABSENCE_OVERLAP':
      return `${name(k.userId)} ist abwesend: ${tage}`
    case 'ASSIGN_DAY_OVERLOAD':
      return `${name(k.userId)}: mehr als die Tagesgrenze (${formatStunden(k.hours ?? 0)} h) am ${tage}`
    case 'ASSIGN_WEEK_OVERLOAD':
      return `${name(k.userId)}: mehr als die Wochengrenze (${formatStunden(k.hours ?? 0)} h) in der Woche ab ${tage}`
    case 'ASSIGN_STEP_NOT_READY':
      return `${schritt(k.stepId)} ist noch nicht freigegeben — in der PWA steht „wartet auf Freigabe"`
    case 'ASSIGN_STEP_PAUSED':
      return `${schritt(k.stepId)} ist pausiert oder die Position angehalten`
    case 'ASSIGN_USER_NO_BOOKING_ROLE':
      return `${name(k.userId)} darf keine Zeit buchen und sieht die Einteilung in der App nicht`
    case 'ASSIGN_AFTER_DUE':
      return `${schritt(k.stepId)}: Zeitraum endet nach der Fälligkeit (${tage})`
    default:
      return k.code
  }
}

export function formatStunden(h: number | null | undefined): string {
  if (h == null) return '—'
  return (Math.round(h * 100) / 100).toLocaleString('de-AT', { maximumFractionDigits: 2 })
}

export function kurzDatum(iso: string | null | undefined): string {
  if (!iso) return '—'
  return tag(iso)
}

export function zeitraumKurz(von: string, bis: string): string {
  if (von === bis) return tag(von)
  return von.slice(5, 7) === bis.slice(5, 7) ? `${von.slice(8, 10)}.–${tag(bis)}` : `${tag(von)}–${tag(bis)}`
}

/** Heute als ISO-Tag in der Ortszeit des Browsers — der Tag, den der Benutzer meint. */
export function heuteIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function plusTage(iso: string, tage: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + tage)
  return d.toISOString().slice(0, 10)
}

export function montagVon(iso: string): string {
  const d = new Date(`${iso}T00:00:00.000Z`)
  return plusTage(iso, -((d.getUTCDay() + 6) % 7))
}

/** Randfarbe der Karte nach Status (Blatt 11 4.1). */
export function kartenRand(status: string, ueberfaellig: boolean): string {
  if (ueberfaellig) return 'border-red-500'
  switch (status) {
    case 'READY':
      return 'border-sky-500'
    case 'ASSIGNED':
      return 'border-indigo-500'
    case 'IN_PROGRESS':
      return 'border-green-500'
    case 'PAUSED':
      return 'border-yellow-500'
    case 'DONE':
      return 'border-blue-300 opacity-70'
    default:
      return 'border-border'
  }
}

export const STATUS_PUNKT: Record<string, string> = {
  PLANNED: 'bg-muted-foreground/40',
  READY: 'bg-sky-500',
  ASSIGNED: 'bg-indigo-500',
  IN_PROGRESS: 'bg-green-500',
  PAUSED: 'bg-yellow-500',
  DONE: 'bg-blue-400',
  CANCELLED: 'bg-red-400',
}
